import { CONSTANTS } from 'clink-components';

/** rgba() from #rrggbb (avoids @mui alpha in Jest / thin deps) */
export const hexAlpha = (hex, a) => {
  const h = String(hex).replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (v.length !== 6) return hex;
  const r = Number.parseInt(v.slice(0, 2), 16);
  const g = Number.parseInt(v.slice(2, 4), 16);
  const b = Number.parseInt(v.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const {
  approvedGreen,
  boqAccent,
  boqAccentHover,
  boqRefineAttentionBg,
  borderGray,
  clinkRed,
  inkBlack,
  lockedGray,
  lockedGrayBg,
  mediumGray,
  mutedGray,
  white,
} = CONSTANTS.colors.general;

const refinePromptCardSx = {
  mb: 2,
  px: { xs: 2, md: 5 },
  py: 2,
  border: `1px solid ${borderGray}`,
  borderRadius: '12px',
  backgroundColor: white,
  boxShadow: 'none',
};

const refinePromptContentSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '24px minmax(0, 1fr) auto' },
  alignItems: 'flex-start',
  columnGap: 1.5,
  rowGap: 1,
};

const refinePromptIconSx = {
  color: boqAccent,
  fontSize: 22,
  gridRow: { md: '1 / span 2' },
  mt: { md: 1.25 },
};

const refinePromptInputSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 42,
    borderRadius: '8px',
    backgroundColor: white,
  },
};

const refinePromptButtonSx = {
  minHeight: 38,
  minWidth: 114,
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  color: white,
  backgroundColor: boqAccent,
  boxShadow: 'none',
  mt: { md: 0.25 },
  '&:hover': {
    backgroundColor: boqAccentHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: white,
    backgroundColor: lockedGray,
  },
};

const refinePromptHelperSx = {
  mt: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  color: mediumGray,
  fontSize: '12px',
};

const refineProgressRowSx = {
  mt: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
};

const refineProgressSx = {
  flex: '0 0 160px',
  height: 3,
  overflow: 'hidden',
  borderRadius: '3px',
  backgroundColor: borderGray,
  '@keyframes boqRefineIndeterminate': {
    '0%': {
      transform: 'translateX(-100%)',
    },
    '100%': {
      transform: 'translateX(350%)',
    },
  },
  '& .MuiLinearProgress-bar': {
    borderRadius: '3px',
    backgroundColor: boqAccent,
  },
  // Replace MUI's default indeterminate cycle (moves, then holds still)
  // with a continuous sweep so the bar never looks frozen between polls.
  '& .MuiLinearProgress-bar1Indeterminate': {
    width: '35%',
    left: 0,
    right: 'auto',
    animation: 'boqRefineIndeterminate 1.15s linear infinite',
  },
  '& .MuiLinearProgress-bar2Indeterminate': {
    display: 'none',
  },
};

const refineProgressTextSx = {
  color: mediumGray,
  fontSize: '12px',
};

/** Same teal as the Draft chip and editable notice. Errors stay on the red alert. */
const refineReviewBannerSx = {
  mb: 2,
  px: 2.5,
  py: 1.75,
  border: `1px solid ${hexAlpha(boqAccent, 0.35)}`,
  borderRadius: '12px',
  backgroundColor: hexAlpha(boqAccent, 0.1),
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

const refineReviewIconSx = {
  width: 36,
  height: 36,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: boqAccent,
  backgroundColor: white,
  flexShrink: 0,
};

const refineReviewCopySx = {
  flex: '1 1 360px',
  minWidth: 0,
};

const refineReviewTitleSx = {
  color: inkBlack,
  fontSize: '14px',
  fontWeight: 700,
};

const refineReviewBodySx = {
  color: mediumGray,
  fontSize: '12px',
  mt: 0.25,
};

const refineReviewActionsSx = {
  ml: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const refineReviewRejectAllSx = {
  color: inkBlack,
  textTransform: 'none',
  fontWeight: 600,
};

const refineReviewAcceptAllSx = {
  color: inkBlack,
  borderColor: borderGray,
  backgroundColor: white,
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    borderColor: boqAccent,
    backgroundColor: white,
  },
};

/** Neutral outcome summary (run completed, nothing to review) — not an error state. */
const refineSummaryBannerSx = {
  mb: 2,
  px: 2.5,
  py: 1.75,
  border: `1px solid ${borderGray}`,
  borderRadius: '12px',
  backgroundColor: lockedGrayBg,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

const refineSummaryIconSx = {
  width: 36,
  height: 36,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: mediumGray,
  backgroundColor: white,
  flexShrink: 0,
};

const refineSummaryDismissSx = {
  ml: 'auto',
  color: inkBlack,
  borderColor: borderGray,
  backgroundColor: white,
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    borderColor: boqAccent,
    backgroundColor: white,
  },
};

const refineDiffCellSx = {
  width: '100%',
  minWidth: 0,
  px: 1,
  py: 0.5,
  border: `1px solid ${clinkRed}`,
  borderRadius: '6px',
  backgroundColor: boqRefineAttentionBg,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  lineHeight: 1.15,
};

const refineDiffBeforeSx = {
  color: mutedGray,
  fontSize: '11px',
  textDecoration: 'line-through',
};

const refineDiffAfterSx = {
  color: clinkRed,
  fontSize: '13px',
  fontWeight: 700,
};

const refineRowActionsSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.5,
  width: '100%',
};

const refineAcceptIconButtonSx = {
  width: 28,
  height: 28,
  border: `1px solid ${borderGray}`,
  borderRadius: '6px',
  color: approvedGreen,
};

const refineRejectIconButtonSx = {
  width: 28,
  height: 28,
  border: `1px solid ${borderGray}`,
  borderRadius: '6px',
  color: clinkRed,
};

export {
  refinePromptCardSx,
  refinePromptContentSx,
  refinePromptIconSx,
  refinePromptInputSx,
  refinePromptButtonSx,
  refinePromptHelperSx,
  refineProgressRowSx,
  refineProgressSx,
  refineProgressTextSx,
  refineReviewBannerSx,
  refineReviewIconSx,
  refineReviewCopySx,
  refineReviewTitleSx,
  refineReviewBodySx,
  refineReviewActionsSx,
  refineReviewRejectAllSx,
  refineReviewAcceptAllSx,
  refineSummaryBannerSx,
  refineSummaryIconSx,
  refineSummaryDismissSx,
  refineDiffCellSx,
  refineDiffBeforeSx,
  refineDiffAfterSx,
  refineRowActionsSx,
  refineAcceptIconButtonSx,
  refineRejectIconButtonSx,
};
